import React from "react";
import { useTranslation } from "react-i18next";
import { PiWarningCircleLight } from "react-icons/pi";
import { useAppDispatch } from "../../store";
import { showModal, hideModal } from "../../store/actions/modalActions";
import {
  AIProgressPanel,
  AIRun,
  initialAIRun,
  stagesFromLegacySteps,
} from "../shared/brand/aiProgress";

export interface Step {
  step: string;
  status: "Pending" | "In Progress" | "Completed" | "Failed";
  messages: string[];
  icon?: React.ReactNode;
  elapsedSeconds?: number;
  iconIdentifier?: string;
}

interface ProgressTrackerProps {
  steps: Step[];
  isComplete: boolean;
  title?: string;
  /** Header once the run is over. Defaults to "Matter Creation Complete". */
  completedTitle?: string;
  caseId?: string;
  matterId?: string;
  correlationId?: string;
  isPreparingDoc?: boolean;
  isConnecting?: boolean;
  isRunning?: boolean;
  backendPercentage?: number;
  onCancel?: () => void;
}

/**
 * Matter creation / analysis progress.
 *
 * A thin adapter over the shared AI panel. What used to be here, and is not
 * coming back:
 *
 *  - **A 60-line icon sniffer.** `stepName.toLowerCase().includes("analysing")`
 *    down thirty branches, so any copy change in the engine silently fell
 *    through to a generic document icon. Icons now come from a stage's STATUS,
 *    which cannot go stale.
 *  - **A step-count percentage over a pre-drawn list.** The engine's
 *    `_build_active_steps()` decides the real pipeline from the user's AI
 *    toggles, so a fixed five-step mirror always under-reported. The list is
 *    no longer pre-drawn (see `MatterReducer`) and the number is the engine's
 *    or there isn't one.
 *  - `w-[50vw]`, the `from-info-500 to-info-900` gradient bar, the
 *    `bg-gray-50 dark:bg-gray-900` discs masking the connector rail, the
 *    untranslated raw status pills, and the `console.log`s.
 */
const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  steps,
  isComplete,
  title = "Creating Matter",
  completedTitle,
  caseId,
  matterId,
  correlationId,
  isConnecting,
  isPreparingDoc,
  isRunning,
  backendPercentage = 0,
  onCancel,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [elapsedTime, setElapsedTime] = React.useState(0);

  /*
    The run's start is written to localStorage by whoever kicked it off, so a
    reload mid-creation resumes the clock instead of restarting it at zero.
  */
  const storageKey =
    matterId && correlationId
      ? `matter_creation_start:${correlationId}`
      : caseId
        ? `case_creation_start:${caseId}`
        : "";

  React.useEffect(() => {
    if (!storageKey) return;
    const read = () => {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return;
      const startMs = new Date(stored).getTime();
      if (!Number.isFinite(startMs)) return;
      setElapsedTime(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    };
    read();
    const interval = setInterval(read, 1000);
    return () => clearInterval(interval);
  }, [storageKey]);

  const handleCancelAnalysis = () => {
    dispatch(
      showModal({
        showModal: true,
        type: "confirmation",
        title: t("Cancel analysis"),
        message: t(
          "This will stop the current process and any data created during this process may be deleted."
        ),
        icon: <PiWarningCircleLight className="w-6 h-6 text-warning-600" />,
        variant: "danger",
        confirmLabel: t("Cancel analysis"),
        onConfirm: () => {
          dispatch(hideModal());
          onCancel?.();
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  const run: AIRun = React.useMemo(() => {
    const stages = stagesFromLegacySteps(steps);
    const failed = stages.some((s) => s.status === "failed");
    return {
      ...initialAIRun,
      status: isComplete
        ? "completed"
        : failed
          ? "failed"
          : stages.length
            ? "running"
            : "connecting",
      stages: isComplete ? stages.map((s) => ({ ...s, status: "done" as const })) : stages,
      // Engine-reported ONLY. Zero is what the reducer holds before the engine
      // has sent a number, and "unknown" is not "nothing done yet".
      percent: isComplete ? 100 : backendPercentage > 0 ? Math.min(100, Math.round(backendPercentage)) : null,
      message: stages.length ? stages[stages.length - 1].message ?? null : null,
      elapsedSeconds: elapsedTime,
    };
  }, [steps, isComplete, backendPercentage, elapsedTime]);

  return (
    <AIProgressPanel
      run={run}
      layout="centered"
      title={t(title)}
      completedTitle={completedTitle ?? t("Matter creation complete")}
      idleMessage={
        isPreparingDoc || isConnecting || isRunning
          ? t("Connecting to the processing pipeline…")
          : undefined
      }
      onCancel={onCancel ? handleCancelAnalysis : undefined}
    />
  );
};

export default ProgressTracker;
